import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination, PaginationContent, PaginationItem } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCents } from '@/lib/format/currency';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import { formatMetric } from '@/lib/social-analytics/formatMetric';

/** CPM-only contract. Deliberately excludes general analytics media metrics. */
export type CpmSubmissionRow = {
  id: string;
  platform: string;
  url: string;
  status?: string | null;
  disqualified: boolean;
  payout_qualified_views?: number | null;
  accumulated_payout_cents?: number | null;
  last_cpm_synced_at?: string | null;
  account?: { id: string; platform: string; username: string; display_name?: string | null } | null;
  campaign?: { id: string; name?: string | null } | null;
};

type PaginationState = { page: number; page_size: number; page_count: number; total: number };
type Props = {
  rows: readonly CpmSubmissionRow[];
  loading: boolean;
  error?: boolean;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  onRetry?: () => void;
};

const safeExternalUrl = (value: string | null | undefined) => {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
};

const safeCampaignPath = (documentId: string | null | undefined) => {
  if (!documentId || /^\d+$/.test(documentId) || !/^[A-Za-z0-9_-]+$/.test(documentId)) return null;
  return `/campaigns/${encodeURIComponent(documentId)}`;
};

const cpmStatus = (row: Pick<CpmSubmissionRow, 'status' | 'disqualified'>) => {
  if (row.disqualified) return 'disqualified';
  if (row.status === 'approved' || row.status === 'disapproved') return row.status;
  if (row.status === 'neutral' || row.status === null || row.status === undefined) return 'pending';
  return 'unknown';
};

const StatusBadge = ({ row }: { row: CpmSubmissionRow }) => {
  const { t } = useTranslation();
  const status = cpmStatus(row);
  const className = status === 'approved'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : status === 'disapproved' || status === 'disqualified'
      ? 'border-destructive/30 bg-destructive/10 text-destructive'
      : status === 'pending'
        ? 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300'
        : 'border-border bg-muted/60 text-muted-foreground';
  return <Badge variant="outline" className={className}>{t(`analytics.cpm.status.${status}`)}</Badge>;
};

const Payout = ({ cents }: { cents: number | null | undefined }) => (
  <>{Number.isFinite(cents) ? formatCents(cents, 'BRL') : '—'}</>
);

export const CpmCampaignsTable = ({ rows, loading, error = false, pagination, onPageChange, onRetry }: Props) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const page = pagination?.page ?? 1;
  const pageCount = pagination?.page_count ?? 0;

  return <section aria-labelledby="cpm-campaigns-title" className="overflow-hidden rounded-xl border border-border/70 bg-card">
    <div className="flex flex-col gap-1 border-b border-border/60 px-4 py-3 sm:px-5">
      <h2 id="cpm-campaigns-title" className="text-base font-semibold">{t('analytics.cpm.title')}</h2>
      <p className="text-xs leading-5 text-muted-foreground">{t('analytics.cpm.payoutQualifiedDescription')}</p>
    </div>
    {error && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-muted-foreground"><span>{t('analytics.cpm.error')}</span>{onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>{t('analytics.error.retry')}</Button>}</div>}
    {loading && rows.length === 0 && !error ? <div role="status" className="space-y-3 p-5"><p className="text-sm text-muted-foreground">{t('analytics.cpm.loading')}</p><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div> : rows.length === 0 ? !error && <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('analytics.cpm.empty')}</p> : <div className="hidden lg:block"><Table><TableHeader><TableRow><TableHead>{t('analytics.cpm.columns.campaign')}</TableHead><TableHead>{t('analytics.cpm.columns.account')}</TableHead><TableHead>{t('analytics.cpm.columns.status')}</TableHead><TableHead>{t('analytics.cpm.payoutQualifiedViews')}</TableHead><TableHead>{t('analytics.cpm.columns.accumulatedPayout')}</TableHead><TableHead>{t('analytics.cpm.columns.lastSync')}</TableHead></TableRow></TableHeader><TableBody>{rows.map((row) => {
      const campaignPath = safeCampaignPath(row.campaign?.id);
      const postUrl = safeExternalUrl(row.url);
      const campaignName = row.campaign?.name || t('analytics.cpm.unnamedCampaign');
      return <TableRow key={row.id}>
        <TableCell className="max-w-[260px]"><div className="flex min-w-0 items-center gap-2">{campaignPath ? <Link to={campaignPath} className="truncate font-medium hover:underline">{campaignName}</Link> : <span className="truncate font-medium">{campaignName}</span>}{postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" aria-label={t('analytics.cpm.openPost')} className="shrink-0 text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</div></TableCell>
        <TableCell><div className="font-medium">@{row.account?.username || '—'}</div><div className="text-xs text-muted-foreground">{t(`analytics.platforms.${row.account?.platform ?? row.platform}`)}</div></TableCell>
        <TableCell><StatusBadge row={row} /></TableCell>
        <TableCell className="font-medium tabular-nums">{formatMetric(row.payout_qualified_views ?? null, locale)}</TableCell>
        <TableCell className="font-medium tabular-nums"><Payout cents={row.accumulated_payout_cents} /></TableCell>
        <TableCell className="whitespace-nowrap text-xs">{formatAnalyticsSyncDate(row.last_cpm_synced_at, locale, '—')}</TableCell>
      </TableRow>;
    })}</TableBody></Table></div>}
    {pagination && onPageChange && (rows.length > 0 || !error) && <div className="border-t border-border/60 px-4 py-3"><Pagination><PaginationContent><PaginationItem><Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>{t('analytics.pagination.previous')}</Button></PaginationItem><PaginationItem><span className="px-2 text-xs tabular-nums text-muted-foreground">{t('analytics.pagination.page', { page, count: Math.max(1, pageCount) })}</span></PaginationItem><PaginationItem><Button type="button" variant="outline" size="sm" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}>{t('analytics.pagination.next')}</Button></PaginationItem></PaginationContent></Pagination></div>}
  </section>;
};
